import { Callout } from "@octopusdeploy/design-system-components";
import { TriggerFilterType, type TriggerGitFilterResource, type TriggerResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import React, { useMemo } from "react";
import { ExternalLinkIcon } from "~/components/Navigation/ExternalLink/ExternalLink";
import InternalLink from "~/components/Navigation/InternalLink";
import type { RelatedTriggersDependencies } from "../ProcessCallouts/CalloutReferencedPackageFeedTriggerConnections";
const getRelatedGitTriggersForPrimaryDependency = (dependencies: RelatedTriggersDependencies | undefined): TriggerResource[] => {
    if (!dependencies)
        return [];
    return getGitTriggersReferencingDependency(dependencies.projectTriggers, dependencies.deploymentActionSlug, "");
};
const getGitTriggersReferencingDependency = (triggers: TriggerResource[] | Readonly<TriggerResource[]>, deploymentActionSlug: string, gitDependencyName: string) => {
    return triggers.filter((trigger) => {
        if (trigger.Filter.FilterType !== TriggerFilterType.GitFilter)
            return false;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const triggerSources = (trigger.Filter as TriggerGitFilterResource).Sources;
        return triggerSources?.some((source) => source.DeploymentActionSlug === deploymentActionSlug && source.GitDependencyName === gitDependencyName);
    });
};
interface GitTriggerCalloutProps {
    spaceId: string;
    projectSlug: string;
    text: ReactNode;
}
interface SingleRelatedGitTriggerCalloutProps extends GitTriggerCalloutProps {
    triggerId: string;
}
function SingleRelatedGitTriggerCallout({ text, spaceId, projectSlug, triggerId }: SingleRelatedGitTriggerCalloutProps) {
    return (<>
            <Callout type={"information"} hideTitle>
                {text}{" "}
                <InternalLink to={links.releaseCreationGitTriggerPage.generateUrl({ spaceId, projectSlug, triggerId })} openInSelf={false}>
                    View trigger
                    <ExternalLinkIcon />
                </InternalLink>
            </Callout>
        </>);
}
function MultipleRelatedGitTriggersCallout({ text, spaceId, projectSlug }: GitTriggerCalloutProps) {
    return (<>
            <Callout type={"information"} hideTitle>
                {text}{" "}
                <InternalLink to={links.triggersPage.generateUrl({ spaceId, projectSlug })} openInSelf={false}>
                    View triggers
                    <ExternalLinkIcon />
                </InternalLink>
            </Callout>
        </>);
}
interface RelatedGitTriggerCalloutProps {
    dependencies: RelatedTriggersDependencies;
}
export function RelatedGitTriggerCallout({ dependencies }: RelatedGitTriggerCalloutProps) {
    const { spaceId, projectSlug } = dependencies;
    const relatedTriggers = useMemo(() => getRelatedGitTriggersForPrimaryDependency(dependencies), [dependencies]);
    if (relatedTriggers.length === 0) {
        return null;
    }
    const uniqueTriggerCount = new Set(relatedTriggers.map((trigger) => trigger.Id)).size;
    if (uniqueTriggerCount === 1) {
        const triggerId = relatedTriggers[0].Id;
        return <SingleRelatedGitTriggerCallout triggerId={triggerId} text={"This repository is connected to a Git trigger."} spaceId={spaceId} projectSlug={projectSlug}/>;
    }
    else {
        return <MultipleRelatedGitTriggersCallout text={"This repository is connected to Git triggers."} spaceId={spaceId} projectSlug={projectSlug}/>;
    }
}
